export interface IndexRange {
  start: number;
  end: number;
}

export interface IndicatorIndices {
  indexName: string;
  /** Segment positions on the gradient bar, normalised to 0-1. */
  breakpoints: IndexRange[];
  /** Value ranges for each index; each covers `start <= value < end`. */
  displayValues: IndexRange[];
  names: string[];
}

export const normalizeIndicatorName = (name?: string | null) =>
  name?.trim().toLocaleLowerCase() ?? "";

export const indicatorNameMatches = (
  candidateName: string | undefined | null,
  indicatorName: string | undefined | null
) => {
  const candidate = normalizeIndicatorName(candidateName);
  const expected = normalizeIndicatorName(indicatorName);

  return candidate !== "" && expected !== "" && candidate === expected;
};

export const calculatePESIndices = (
  minValue: number,
  maxValue: number
): IndicatorIndices => {
  // Break into 5 equal sized chunks

  return {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: 0.2 },
      { start: 0.2, end: 0.4 },
      { start: 0.4, end: 0.6 },
      { start: 0.6, end: 0.8 },
      { start: 0.8, end: 1 },
    ],
    displayValues: [
      { start: minValue, end: minValue + 0.2 * (maxValue - minValue) },
      {
        start: minValue + 0.2 * (maxValue - minValue),
        end: minValue + 0.4 * (maxValue - minValue),
      },
      {
        start: minValue + 0.4 * (maxValue - minValue),
        end: minValue + 0.6 * (maxValue - minValue),
      },
      {
        start: minValue + 0.6 * (maxValue - minValue),
        end: minValue + 0.8 * (maxValue - minValue),
      },
      { start: minValue + 0.8 * (maxValue - minValue), end: maxValue },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  };
};

// Keyed by the stable indicator-definition `name` (not the editable display
// name). Lookups are case-insensitive via `normalizeIndicatorName`.
const hardcodedIndices: Record<string, IndicatorIndices> = {
  P: {
    indexName: "RB209",
    breakpoints: [
      // P min value = 0, max value = 52
      { start: 0, end: 9 / 52 },
      { start: 9 / 52, end: 15 / 52 },
      { start: 15 / 52, end: 25 / 52 },
      { start: 25 / 52, end: 45 / 52 },
      { start: 45 / 52, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 9 },
      { start: 9, end: 15 },
      { start: 15, end: 25 },
      { start: 25, end: 45 },
      { start: 45, end: 52 },
    ],
    names: ["0", "1", "2", "3", "4"],
  },
  K: {
    indexName: "RB209",
    breakpoints: [
      { start: 0, end: 60 / 601 },
      { start: 60 / 601, end: 120 / 601 },
      { start: 120 / 601, end: 180 / 601 },
      { start: 180 / 601, end: 240 / 601 },
      { start: 240 / 601, end: 400 / 601 },
      { start: 400 / 601, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 60 },
      { start: 60, end: 120 },
      { start: 120, end: 180 },
      { start: 180, end: 240 },
      { start: 240, end: 400 },
      { start: 400, end: 601 },
    ],
    names: ["0", "1", "2-", "2+", "3", "4"],
  },
  Mg: {
    indexName: "RB209",
    breakpoints: [
      { start: 0, end: 25 / 275 },
      { start: 25 / 275, end: 50 / 275 },
      { start: 50 / 275, end: 100 / 275 },
      { start: 100 / 275, end: 175 / 275 },
      { start: 175 / 275, end: 250 / 275 },
      { start: 251 / 275, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 25 },
      { start: 25, end: 50 },
      { start: 50, end: 100 },
      { start: 100, end: 175 },
      { start: 175, end: 250 },
      { start: 250, end: 251 },
    ],
    names: ["0", "1", "2", "3", "4", "5"],
  },
  "Field water content": {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: 15 / 32 },
      { start: 15 / 32, end: 19 / 32 },
      { start: 19 / 32, end: 24 / 32 },
      { start: 24 / 32, end: 29 / 32 },
      { start: 29 / 32, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 15 },
      { start: 15, end: 20 },
      { start: 20, end: 25 },
      { start: 25, end: 30 },
      { start: 30, end: 30 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "Field soil content": {
    indexName: "PES",
    breakpoints: [
      { start: (68 - 68) / (85 - 68), end: (71 - 68) / (85 - 68) },
      { start: (71 - 68) / (85 - 68), end: (74 - 68) / (85 - 68) },
      { start: (74 - 68) / (85 - 68), end: (80 - 68) / (85 - 68) },
      { start: (80 - 68) / (85 - 68), end: (83 - 68) / (85 - 68) },
      { start: (83 - 68) / (85 - 68), end: 1 },
    ],
    displayValues: [
      { start: 68, end: 71 },
      { start: 71, end: 74 },
      { start: 74, end: 80 },
      { start: 80, end: 83 },
      { start: 83, end: 85 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "Microbial Biomass": {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: 200 / 1000 },
      { start: 200 / 1000, end: 400 / 1000 },
      { start: 400 / 1000, end: 600 / 1000 },
      { start: 600 / 1000, end: 800 / 1000 },
      { start: 800 / 1000, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 200 },
      { start: 200, end: 400 },
      { start: 400, end: 600 },
      { start: 600, end: 800 },
      { start: 800, end: 1000 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "OM LOI": {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: 3 / 15 },
      { start: 3 / 15, end: 6 / 15 },
      { start: 6 / 15, end: 9 / 15 },
      { start: 9 / 15, end: 12 / 15 },
      { start: 12 / 15, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 3 },
      { start: 3, end: 6 },
      { start: 6, end: 9 },
      { start: 9, end: 12 },
      { start: 12, end: 15 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "WHC water content": {
    indexName: "PES",
    breakpoints: [
      { start: (26 - 26) / (42 - 26), end: (28 - 26) / (42 - 26) },
      { start: (28 - 26) / (42 - 26), end: (32 - 26) / (42 - 26) },
      { start: (32 - 26) / (42 - 26), end: (36 - 26) / (42 - 26) },
      { start: (36 - 26) / (42 - 26), end: (40 - 26) / (42 - 26) },
      { start: (40 - 26) / (42 - 26), end: 1 },
    ],
    displayValues: [
      { start: 26, end: 28 },
      { start: 28, end: 32 },
      { start: 32, end: 36 },
      { start: 36, end: 40 },
      { start: 40, end: 42 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "WHC soil content": {
    indexName: "PES",
    breakpoints: [
      {
        start: (60 - 60) / (72 - 60),
        end: (62 - 60) / (72 - 60),
      },
      {
        start: (62 - 60) / (72 - 60),
        end: (64 - 60) / (72 - 60),
      },
      {
        start: (64 - 60) / (72 - 60),
        end: (66 - 60) / (72 - 60),
      },
      {
        start: (66 - 60) / (72 - 60),
        end: (70 - 60) / (72 - 60),
      },
      {
        start: (70 - 60) / (72 - 60),
        end: (72 - 60) / (72 - 60),
      },
    ],
    displayValues: [
      { start: 60, end: 62 },
      { start: 62, end: 64 },
      { start: 64, end: 66 },
      { start: 66, end: 70 },
      { start: 70, end: 72 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "Ammonium nitrogen": {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: 2.5 / 6.5 },
      { start: 2.5 / 6.5, end: 3.7 / 6.5 },
      { start: 3.7 / 6.5, end: 4.8 / 6.5 },
      { start: 4.8 / 6.5, end: 6 / 6.5 },
      { start: 6 / 6.5, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 2.5 },
      { start: 2.5, end: 3.7 },
      { start: 3.7, end: 4.8 },
      { start: 4.8, end: 6 },
      { start: 6, end: 6.5 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  "Nitrate nitrogen": {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: 9 / 40 },
      { start: 9 / 40, end: 17.5 / 40 },
      { start: 17.5 / 40, end: 26 / 40 },
      { start: 26 / 40, end: 35 / 40 },
      { start: 35 / 40, end: 1 },
    ],
    displayValues: [
      { start: 0, end: 9 },
      { start: 9, end: 17.5 },
      { start: 17.5, end: 26 },
      { start: 26, end: 35 },
      { start: 35, end: 40 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
  pH: {
    indexName: "PES",
    breakpoints: [
      { start: 0, end: (5.6 - 5) / (8 - 5) },
      { start: (5.6 - 5) / (8 - 5), end: (6.2 - 5) / (8 - 5) },
      { start: (6.2 - 5) / (8 - 5), end: (6.8 - 5) / (8 - 5) },
      { start: (6.8 - 5) / (8 - 5), end: (7.4 - 5) / (8 - 5) },
      { start: (7.4 - 5) / (8 - 5), end: 1 },
    ],
    displayValues: [
      { start: 5, end: 5.6 },
      { start: 5.6, end: 6.2 },
      { start: 6.2, end: 6.8 },
      { start: 6.8, end: 7.4 },
      { start: 7.4, end: 8 },
    ],
    names: ["[1]", "[2]", "[3]", "[4]", "[5]"],
  },
};

/**
 * Index definitions for an indicator, selected by its stable
 * indicator-definition name. Indicators without hardcoded ranges fall back to
 * five equal PES bands across the trained indicator's min/max.
 */
export const getIndicatorIndices = (
  indicatorName: string | undefined | null,
  minValue: number,
  maxValue: number
): IndicatorIndices => {
  const hardcoded = Object.entries(hardcodedIndices).find(([name]) =>
    indicatorNameMatches(name, indicatorName)
  )?.[1];
  return hardcoded ?? calculatePESIndices(minValue, maxValue);
};

/**
 * Position of `value` in `indices.displayValues`. Values below the first range
 * clamp to the first index, and values at or above the last range's end clamp
 * to the last index.
 */
export const getIndexForValue = (
  indices: IndicatorIndices,
  value: number
): number => {
  const { displayValues } = indices;
  if (value < displayValues[0].start) {
    return 0;
  }
  const found = displayValues.findIndex(
    (range) => range.start <= value && value < range.end
  );
  return found === -1 ? displayValues.length - 1 : found;
};

/** Human-readable range covered by index `index`, e.g. "<9", "9 to <15", "≥45". */
export const getIndexRangeText = (
  indices: IndicatorIndices,
  index: number,
  decimalPlaces: number
): string => {
  const { displayValues } = indices;
  const range = displayValues[index];
  if (index === 0) {
    return `<${range.end.toFixed(decimalPlaces)}`;
  }
  if (index === displayValues.length - 1) {
    return `≥${range.start.toFixed(decimalPlaces)}`;
  }
  return `${range.start.toFixed(decimalPlaces)} to <${range.end.toFixed(
    decimalPlaces
  )}`;
};
